<template>
	<span
		v-if="invisible && to"
		:id="id"
		:class="buttonClasses"
		role="link"
		tabindex="0"
		@click="openInvisibleLink"
		@keydown.enter="openInvisibleLink"
	>
		<slot />
	</span>
	<HokLink
		v-else-if="to"
		:id="id"
		:to="to"
		:link-type="linkType"
		:name="name"
		:value="value"
		:class="buttonClasses"
		:styling="unstyledDisplay ? 'unstyled' : undefined"
		:target="target"
	>
		<slot />
	</HokLink>
	<button
		v-else
		:id="id"
		ref="button"
		:name="name"
		:value="value"
		:type="submit ? 'submit' : 'button'"
		:class="buttonClasses"
		:disabled="disabled"
		@click="click"
		@touchstart="$emit('touchstart', $event)"
		@touchend="$emit('touchend', $event)"
	>
		<slot />
	</button>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import HokLink from './HokLink.vue';

export default defineComponent({
	name: 'HokButton',
	components: { HokLink },
	emits: ['click', 'touchstart', 'touchend'],
	data() {
		return {
			processing: false,
			button: null as HTMLElement | null
		};
	},
	mounted() {
		this.button = this.$refs.button as HTMLElement;
	},
	computed: {
		buttonClasses() {
			const buttonClasses = this.unstyledDisplay
				? ['rounded-full']
				: ['inline-flex rounded-full justify-center items-center'];

			if ((this.fullwidth === 'mobile' && this.$isMobile.phone) || this.fullwidth === 'always') {
				buttonClasses.push('w-full');
			}

			if (this.disabled) {
				if (this.outline) {
					buttonClasses.push('border-grey-light');
					buttonClasses.push('text-grey-light');
				} else if (!this.isText) {
					buttonClasses.push('bg-grey-light');
					buttonClasses.push('border-grey-light');
					buttonClasses.push('text-white');
				}
				buttonClasses.push('cursor-not-allowed');
			} else {
				buttonClasses.push('cursor-pointer');
			}
			// if button should be a text button
			if (this.isText) {
				buttonClasses.push('border-0');
				buttonClasses.push('bg-transparent');
				buttonClasses.push(this.fontWeigthClass ? this.fontWeigthClass : 'font-bold');
				if (this.disabled) {
					buttonClasses.push('text-grey');
				} else {
					switch (this.color) {
						case 'main':
							buttonClasses.push('text-main');
							break;
						case 'blue':
							buttonClasses.push('text-blue');
							break;
						case 'yellow':
							buttonClasses.push('text-yellow');
							break;
						case 'purple':
							buttonClasses.push('text-purple');
							break;
						case 'business':
							buttonClasses.push('text-main');
							break;
						case 'grey':
							buttonClasses.push('text-grey-light');
							break;
						case 'blue-grey':
							buttonClasses.push('text-blue-grey');
							break;
						case 'white':
							buttonClasses.push('text-white');
							break;
						case 'coral':
							buttonClasses.push('text-coral');
							break;
						case 'text':
							buttonClasses.push('text-text');
							break;
						case 'grey-medium':
							buttonClasses.push('text-grey-medium');
							break;
						default:
						// Nothing
					}
				}
				if (this.underline) {
					buttonClasses.push('underline');
				}
			} else {
				// Set default styling for button
				buttonClasses.push('border');
				switch (this.size) {
					case 'small':
						buttonClasses.push('px-3');
						buttonClasses.push('py-1');
						buttonClasses.push('leading-tight');
						buttonClasses.push(this.fontWeigthClass ? this.fontWeigthClass : 'font-normal');
						break;
					case 'medium':
						buttonClasses.push('px-8');
						buttonClasses.push('py-2');
						buttonClasses.push(this.fontWeigthClass ? this.fontWeigthClass : 'font-bold');
						break;
					case 'big':
						buttonClasses.push('px-12');
						buttonClasses.push('py-3');
						buttonClasses.push(this.fontWeigthClass ? this.fontWeigthClass : 'font-bold');
						break;
					case 'chips':
						buttonClasses.push('pl-4 pr-2 py-2');
						buttonClasses.push(this.fontWeigthClass ? this.fontWeigthClass : 'font-bold');
						break;
					case 'rounded':
						buttonClasses.push('rounded-full');
						buttonClasses.push('p-3');
						buttonClasses.push('w-10 h-10');
						break;
					default:
					// nothing!
				}
				// if outline active and not disabled only set border- and text-color
				if (this.outline && !this.disabled) {
					// buttonClasses.push('outline');
					// buttonClasses.push('bg-transparent');
					switch (this.color) {
						case 'main':
							buttonClasses.push('border-main');
							buttonClasses.push('text-main');
							buttonClasses.push('hover:bg-main');
							buttonClasses.push('hover:text-white');
							break;
						case 'blue':
							buttonClasses.push('border-blue');
							buttonClasses.push('text-blue');
							buttonClasses.push('hover:bg-blue');
							buttonClasses.push('hover:text-white');
							break;
						case 'yellow':
							buttonClasses.push('border-yellow');
							buttonClasses.push('text-yellow');
							buttonClasses.push('hover:bg-yellow');
							buttonClasses.push('hover:text-white');
							break;
						case 'purple':
							buttonClasses.push('border-purple');
							buttonClasses.push('text-purple');
							buttonClasses.push('hover:bg-purple');
							buttonClasses.push('hover:text-white');
							break;
						case 'business':
							buttonClasses.push('border-main');
							buttonClasses.push('text-main');
							buttonClasses.push('hover:bg-main');
							buttonClasses.push('hover:text-white');
							break;
						case 'coral':
							buttonClasses.push('border-coral');
							buttonClasses.push('text-coral');
							buttonClasses.push('hover:bg-coral');
							buttonClasses.push('hover:text-white');
							break;
						case 'coral-white':
							buttonClasses.push('border-white');
							buttonClasses.push('text-white');
							buttonClasses.push('hover:bg-white');
							buttonClasses.push('hover:text-text');
							break;
						case 'grey':
							buttonClasses.push('border-grey-light');
							buttonClasses.push('text-grey-light');
							break;
						case 'blue-grey':
							buttonClasses.push('border-blue-grey');
							buttonClasses.push('text-blue-grey');
							break;
						case 'white':
							buttonClasses.push('border-white');
							buttonClasses.push('text-white');
							buttonClasses.push('hover:bg-white');
							buttonClasses.push('hover:text-blue-grey');
							break;
						case 'text':
							buttonClasses.push('border-text');
							buttonClasses.push('text-text');
							break;
						default:
						// Nothing
					}
				} else if (!this.disabled) {
					// set button styling if not disabled and not outline
					// bg-color, border-color, text-color, and their respective hover states will be set
					switch (this.color) {
						case 'main':
							buttonClasses.push('bg-main');
							buttonClasses.push('border-main');
							buttonClasses.push('hover:border-main-hover');
							buttonClasses.push('hover:bg-main-hover');
							buttonClasses.push('text-white');
							break;
						case 'blue':
							buttonClasses.push('bg-blue');
							buttonClasses.push('border-blue');
							buttonClasses.push('hover:border-blue-hover');
							buttonClasses.push('hover:bg-blue-hover');
							buttonClasses.push('text-white');
							break;
						case 'yellow':
							buttonClasses.push('bg-yellow');
							buttonClasses.push('border-yellow');
							buttonClasses.push('hover:border-yellow-hover');
							buttonClasses.push('hover:bg-yellow-hover');
							buttonClasses.push('text-white');
							break;
						case 'purple':
							buttonClasses.push('bg-purple');
							buttonClasses.push('border-purple');
							buttonClasses.push('hover:border-purple-hover');
							buttonClasses.push('hover:bg-purple-hover');
							buttonClasses.push('text-white');
							break;
						case 'business':
							buttonClasses.push('bg-main');
							buttonClasses.push('border-main');
							buttonClasses.push('hover:border-main-hover');
							buttonClasses.push('hover:bg-main-hover');
							buttonClasses.push('text-white');
							break;
						case 'coral':
							buttonClasses.push('bg-coral');
							buttonClasses.push('border-coral');
							buttonClasses.push('hover:border-coral-hover');
							buttonClasses.push('hover:bg-coral-hover');
							buttonClasses.push('text-white');
							break;
						case 'grey':
							buttonClasses.push('bg-grey-light');
							buttonClasses.push('border-grey-light');
							buttonClasses.push('text-white');
							break;
						case 'blue-grey':
							buttonClasses.push('bg-blue-grey');
							buttonClasses.push('border-blue-grey');
							buttonClasses.push('text-white');
							break;
						case 'white':
							buttonClasses.push('bg-white');
							buttonClasses.push('border-white');
							buttonClasses.push('hover:border-white-hover');
							buttonClasses.push('hover:bg-white-hover');
							buttonClasses.push('text-main');
							break;
						case 'white-text':
							buttonClasses.push('bg-white');
							buttonClasses.push('border-white');
							buttonClasses.push('hover:border-white-hover');
							buttonClasses.push('hover:bg-white-hover');
							buttonClasses.push('text-text');
							break;
						case 'white-main':
							buttonClasses.push('bg-white');
							buttonClasses.push('border-main');
							buttonClasses.push('text-main');
							buttonClasses.push('hover:text-main');
							buttonClasses.push('hover:bg-white');
							break;
						case 'white-grey':
							buttonClasses.push('bg-white');
							buttonClasses.push('border-grey');
							buttonClasses.push('text-black');
							buttonClasses.push('hover:bg-grey-lightest');
							break;
						default:
						// Nothing
					}
				}
			}

			if (this.to && this.positionSet) {
				buttonClasses.push('relative');
			}
			return buttonClasses;
		},
		fontWeigthClass(): string | false {
			if (this.fontWeight)
				switch (this.fontWeight) {
					case 'normal':
						return 'font-normal';
					case 'light':
						return 'font-light';
					case 'bold':
					default:
						return 'font-bold';
				}
			else return false;
		}
	},
	methods: {
		click($event) {
			if (this.disabled) return; // do net emit click event, if button is disabled

			if (this.nothrottling) {
				this.$emit('click', $event);
				return;
			}
			if (!this.processing) {
				this.processing = true;
				this.$emit('click', $event);
				setTimeout(() => {
					this.processing = false;
				}, 1000); // allow another click only after 1 second pause
			}
		},
		async openInvisibleLink() {
			return navigateTo(this.to, { external: true });
		}
	},
	props: {
		id: { type: String },
		name: { type: String },
		linkType: { type: String, required: false },
		value: { type: String, default: undefined },
		fullwidth: {
			type: [String, Boolean],
			default: false,
			validator: (value: string | boolean) => [false, 'mobile', 'always'].includes(value)
		},
		disabled: { type: Boolean, default: false },
		submit: { type: Boolean, default: false },
		isText: { type: Boolean, default: false },
		color: {
			type: String,
			default: 'blue',
			validator: (value: string) =>
				[
					'blue',
					'yellow',
					'main',
					'purple',
					'business',
					'grey',
					'blue-grey',
					'white',
					'white-text',
					'coral',
					'text',
					'grey-medium',
					'white-main',
					'coral-white',
					'white-grey'
				].includes(value)
		},
		size: {
			default: 'medium',
			validator: (value: string) => ['small', 'medium', 'big', 'chips'].includes(value),
			type: String
		},
		fontWeight: {
			default: false,
			validator: (value: string | boolean) => ['light', 'normal', 'bold', false].includes(value),
			type: [String, Boolean]
		},
		to: { type: String, default: undefined },
		outline: { type: Boolean, default: false },
		underline: { type: Boolean, default: false },
		invisible: { type: Boolean, default: false },
		positionSet: { type: Boolean, default: true },
		target: { type: String, required: false },
		nothrottling: { type: Boolean, default: false },
		unstyledDisplay: { type: Boolean, default: false }
	}
});
</script>

<style lang="scss" scoped>
button:not(.bg-transparent),
a:not(.bg-transparent) {
	text-decoration: none;

	&:hover {
		transition: background-color 600ms ease;
	}

	svg {
		fill: #ffffff;
	}

	&:active {
		box-shadow:
			0 0 10px white inset,
			0 1px 0 whitesmoke;
	}
}
</style>
